import { Box, Divider, Typography, CircularProgress, Alert, Button, ToggleButton, ToggleButtonGroup, Chip } from '@mui/material'
import React, { useState, useEffect } from 'react'
import LargeCard from '../../../Shared-ui/SharedComponents/Cards/LargeCard'
import variables from "../../../assets/styles/variables.module.scss"
import ZeaOneService, { ZeaOnePlan, PurchaseRequest } from '../../../Services/ZeaOneService'
import { ShowError, ShowSuccess } from '../../../Shared-ui/Store/MessageReducer/MsgActions'
import { useDispatch, useSelector } from 'react-redux'
import userContext from '../../../Shared-ui/Context/UserContext'
import { useContext } from 'react'
import { GetSession, SetSession } from '../../../Shared-ui/utils/Auth/AuthHelpers'
import { getSubscriptionFromToken, SubscriptionData, getUserIdFromToken } from '../../../utils/jwt'
import axios from 'axios'
import {REGENERATE_ACCESS_TOKEN} from '../../../Services/Endpoint'
import { useNavigate } from "react-router-dom";
import { tab } from '@testing-library/user-event/dist/tab'

type UserSubscriptionProps = {
  transactionId?: string | null; 
};

const UserSubscription: React.FC<UserSubscriptionProps> = (transactionId) => {
    const [plans, setPlans] = useState<ZeaOnePlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedDurations, setSelectedDurations] = useState<{[planId: string]: string}>({});
    const [purchasingPlan, setPurchasingPlan] = useState<string | null>(null);
    const [currentSubscription, setCurrentSubscription] = useState<SubscriptionData | null>(null);
    const dispatch = useDispatch();
    const { user } = useContext(userContext);
    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
        transactionId && reloadSubscription()
        loadCurrentSubscription();
    }, []);


    const reloadSubscription = async () => {
        const token = await GetSession();
        if (token) {
            const userId = await getUserIdFromToken(token);
            if(userId){
                const response = await axios.get(`${REGENERATE_ACCESS_TOKEN}/${userId}`);
                if(response?.data){
                    SetSession(response?.data?.access_token, response?.data.token_type)
                }
                loadCurrentSubscription()
                navigate('/userprofile', {state: {fromZeaone: true}});
            }
        }
    }


    const loadCurrentSubscription = () => {
        try {
            const token = GetSession();
            if (token) {
                const subscription = getSubscriptionFromToken(token);
                setCurrentSubscription(subscription);
            }
        } catch (error) {
            console.error('Error loading current subscription:', error);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch plans from API
            const plansData = await ZeaOneService.getPlans();
            setPlans(plansData);
        } catch (err: any) {
            console.error('Error fetching subscription data:', err);
            setError(err.message || 'Failed to load subscription data');
            dispatch(ShowError('Failed to load subscription data. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const handlePurchasePlan = async (planId: string) => {
        try {
            setPurchasingPlan(planId);
            
            const plan = plans.find(p => p.id === planId);
            const selectedDuration = selectedDurations[planId] || plan?.pricing_tiers[0]?.duration;
            
            if (!selectedDuration) {
                throw new Error('Please select a duration');
            }
            
            if (!user?.email || !user?.full_name) {
                throw new Error('User information not available');
            }
            
            // Use organization_id as user_id if available, otherwise use user.id
            const userId = user.organization_id && user.organization_id !== 'notSelected' 
                ? user.organization_id 
                : user.id || user.email;
            
            // Use organization name as user_name if available, otherwise use user.full_name
            const userName = user.organization_id && user.organization_id !== 'notSelected' 
                ? user.organization_name || user.full_name
                : user.full_name;
            
            const purchaseRequest: PurchaseRequest = {
                user_id: userId,
                user_email: user.email,
                user_name: userName,
                plan_id: planId,
                duration: selectedDuration
            };
            
            const response = await ZeaOneService.createPurchase(purchaseRequest);
            
            // Redirect to payment page using transaction_id
            const paymentUrl = `https://demo-zeaone.zeasale.com/api/api/v1/purchase/${response.transaction_id}`;
            window.location.href = paymentUrl;
            
        } catch (err: any) {
            console.error('Error purchasing plan:', err);
            
            // Handle ZeaOne API error response
            if (err.response?.data) {
                const errorData = err.response.data;
                if (errorData.error === 'active_subscription_exists') {
                    // Use details field for more comprehensive error message
                    const errorMessage = errorData.details || errorData.message || 'You already have an active subscription. Please wait for it to expire or cancel it before purchasing a new one.';
                    dispatch(ShowError(errorMessage));
                } else {
                    dispatch(ShowError(errorData.message || 'Failed to initiate purchase. Please try again.'));
                }
            } else {
                dispatch(ShowError(err.message || 'Failed to initiate purchase. Please try again.'));
            }
        } finally {
            setPurchasingPlan(null);
        }
    };

    const getPlanColor = (index: number) => {
        const colors = [variables.basicColor, variables.starterColor, variables.professionalColor, variables.enterpriseColor];
        return colors[index % colors.length];
    };

    const formatPrice = (price: number, currency: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD'
        }).format(price);
    };

    const getSelectedPrice = (plan: ZeaOnePlan) => {
        const selectedDuration = selectedDurations[plan.id] || plan.pricing_tiers[0]?.duration;
        const selectedTier = plan.pricing_tiers.find(tier => tier.duration === selectedDuration);
        return selectedTier || plan.pricing_tiers[0];
    };

    const handleDurationChange = (planId: string, duration: string) => {
        setSelectedDurations(prev => ({
            ...prev,
            [planId]: duration
        }));
    };

    const formatFeatures = (features: string[], consumableCounts: any) => {
        return features.map(feature => {
            const count = consumableCounts[feature];
            
            // If feature is not in consumable_counts, show just the feature name
            if (count === undefined || count === null) {
                switch(feature) {
                    case 'USERS': return 'User Management';
                    case 'AGENTS': return 'AI Agents';
                    case 'KNOWLEDGE_BASE': return 'Knowledge Base';
                    case 'VOICE_AGENT': return 'Voice Agent';
                    default: return feature.replace('_', ' ');
                }
            }
            
            // If feature is consumable, show count
            const countText = count === -1 ? 'Unlimited' : count.toString();
            
            switch(feature) {
                case 'USERS': return `${countText} User${count !== 1 ? 's' : ''}`;
                case 'AGENTS': return `${countText} AI Agent${count !== 1 ? 's' : ''}`;
                case 'KNOWLEDGE_BASE': return `${countText} Knowledge Base${count !== 1 ? 's' : ''}`;
                case 'VOICE_AGENT': return `${countText} Voice Agent${count !== 1 ? 's' : ''}`;
                default: return `${countText} ${feature.replace('_', ' ')}`;
            }
        });
    };

    const isCurrentPlan = (plan: ZeaOnePlan) => {
        if (!currentSubscription || !currentSubscription.has_active_plan) {
            return false;
        }
        
        // Match by plan name (case insensitive)
        return plan.name.toLowerCase() === currentSubscription.plan_name?.toLowerCase();
    };

    const getCurrentPlanStatus = (plan: ZeaOnePlan) => {
        if (!isCurrentPlan(plan)) {
            return null;
        }

        const subscription = currentSubscription!;
        const endDate = subscription.end_date ? new Date(subscription.end_date) : null;
        const now = new Date();
        
        if (endDate && endDate > now) {
            const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            return {
                status: 'active',
                daysLeft: daysLeft,
                duration: subscription.duration
            };
        }
        
        return {
            status: 'expired',
            daysLeft: 0,
            duration: subscription.duration
        };
    };


    const PlanCard = ({ plan, index }: { plan: ZeaOnePlan; index: number }) => {
        const selectedPrice = getSelectedPrice(plan);
        const features = formatFeatures(plan.selected_features, plan.consumable_counts);
        const hasMultipleDurations = plan.pricing_tiers.length > 1;
        const isCurrent = isCurrentPlan(plan);
        const planStatus = getCurrentPlanStatus(plan);
        
        // Determine card styling based on current plan status
        const cardStyle = isCurrent ? {
            border: `2px solid #4caf50`,
            backgroundColor: '#f8fff8',
            position: 'relative' as const,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(76, 175, 80, 0.15)',
        } : {
            border: '1px solid #e0e0e0',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            }
        };
        
        return (
            <Box sx={{ ...cardStyle, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {isCurrent && (
                    <Chip
                        label={
                            planStatus?.status === 'active' 
                                ? `Current Plan (${planStatus.daysLeft} days left)`
                                : 'Current Plan (Expired)'
                        }
                        color={planStatus?.status === 'active' ? 'success' : 'warning'}
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            zIndex: 1,
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            height: 28,
                        }}
                    />
                )}
                <Box sx={{ position: 'relative', zIndex: 1, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <LargeCard
                        key={plan.id}
                        amount={selectedPrice.amount}
                        duration={selectedPrice.duration}
                        title={plan.name}
                        summary={features}
                        body={[
                            `${formatPrice(selectedPrice.amount, selectedPrice.currency)} Billed ${selectedPrice.duration === 'monthly' ? 'Monthly' : 'Yearly'}`,
                            plan.description
                        ]}
                        color={getPlanColor(index)}
                        action={
                            isCurrent 
                                ? "Current Plan"
                                : purchasingPlan === plan.id 
                                    ? "Processing..." 
                                    : "Purchase This Plan"
                        }
                        onActionClick={() => !isCurrent && handlePurchasePlan(plan.id)}
                        disabled={purchasingPlan === plan.id || isCurrent}
                        pricingTiers={plan.pricing_tiers}
                        selectedDuration={selectedDurations[plan.id]}
                        onDurationChange={(duration) => handleDurationChange(plan.id, duration)}
                        showDurationSelector={hasMultipleDurations}
                    />
                </Box>
            </Box>
        );
    };

    if (loading) {
        return (
            <>
                <Typography variant="h6">Subscription</Typography>
                <Typography variant="body2">
                    View your subscription here.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress />
                </Box>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Typography variant="h6">Subscription</Typography>
                <Typography variant="body2">
                    View your subscription here.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button variant="contained" onClick={fetchData}>
                    Retry
                </Button>
            </>
        );
    }

    return (
        <>
            <Typography variant="h6">Subscription</Typography>
            <Typography variant="body2">
                View your subscription here.
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            {/* Current Subscription Summary */}
            {currentSubscription && currentSubscription.has_active_plan && (
                <Alert 
                    severity="success" 
                    sx={{ mb: 3 }}
                    action={
                        <Chip 
                            label={`${currentSubscription.plan_name} Plan`}
                            color="success"
                            size="small"
                        />
                    }
                >
                    <Typography variant="body2">
                        <strong>Current Plan:</strong> {currentSubscription.plan_name} ({currentSubscription.duration})
                        {currentSubscription.end_date && (
                            <>
                                <br />
                                <strong>Expires:</strong> {new Date(currentSubscription.end_date).toLocaleDateString()}
                            </>
                        )}
                    </Typography>
                </Alert>
            )}
            
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xl: "1fr 1fr 1fr 1fr",
                        lg: "1fr 1fr 1fr 1fr",
                        md: "1fr 1fr",
                    },
                    gap: 2,
                    alignItems: "stretch",
                    mt: 3,
                }}
            >
                {plans.map((plan, index) => (
                    <PlanCard key={plan.id} plan={plan} index={index} />
                ))}
            </Box>
        </>
    );
}

export default UserSubscription